import { SERIES_FREQUENCIES, type SeriesFrequency } from '../constants/index.ts';
import {
  addDaysToDate,
  dayOfWeekOf,
  normalizeStoredRule,
  weekdaysForVisitCount,
  type StoredRuleColumns,
} from '../calculations/recurrence.ts';

/** The hour a booked quote starts at when nobody picked one. */
export const DEFAULT_VISIT_START = '09:00';

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Tomorrow, as a calendar date. The first visit has to be a day someone can
 * still change, and "tomorrow" is the one that needs no explanation.
 */
export function suggestedFirstVisit(today: string): string {
  return addDaysToDate(today, 1);
}

/** The reader's own calendar date, not UTC. */
export function localIsoDate(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function isVisitTime(value: string): boolean {
  return TIME_PATTERN.test(value);
}

/**
 * How long the visit occupies the day.
 *
 * A quote with no time yet still has to land on a calendar, so it gets an
 * hour. Anything shorter than 15 minutes is rounded up to that, which is the
 * shortest visit the schedule will store.
 */
export function visitLengthMinutes(onSiteMinutes: number): number {
  if (!Number.isFinite(onSiteMinutes) || onSiteMinutes <= 0) return 60;
  const rounded = Math.ceil(onSiteMinutes / 15) * 15;
  return Math.max(15, rounded);
}

/** Longest visit the schedule stores — one calendar day. */
export const MAX_VISIT_MINUTES = 1440;

/**
 * Weekdays for a weekly job that comes more than once.
 *
 * The usual pattern (Monday/Wednesday/Friday, and so on) wins when the first
 * visit already falls on one of those days. Otherwise the closest usual day
 * is swapped for the day they picked, so the first visit is that day and not
 * the Monday after it.
 */
export function weekdaysStartingOn(startDow: number, count: number): number[] {
  const visits = Math.min(7, Math.max(1, Math.trunc(count) || 1));
  if (visits === 1) return [startDow];
  const pattern = weekdaysForVisitCount(visits);
  if (pattern.includes(startDow)) return pattern;
  let nearest = pattern[0]!;
  let best = 7;
  for (const day of pattern) {
    const distance = Math.min((day - startDow + 7) % 7, (startDow - day + 7) % 7);
    if (distance < best) {
      best = distance;
      nearest = day;
    }
  }
  return [...pattern.filter((day) => day !== nearest), startDow].sort((left, right) => left - right);
}

export function isSeriesFrequency(value: string | null | undefined): value is SeriesFrequency {
  return typeof value === 'string' && (SERIES_FREQUENCIES as readonly string[]).includes(value);
}

/**
 * Which schedule the acceptance books.
 *
 * A monthly price that covers more than one visit a week is that weekly
 * schedule, even when "How often?" says once a month, every two weeks, or
 * once. Those answers would drop the other visits while each visit is still
 * priced as one share of the week. Otherwise the frequency they said yes to
 * wins, then the "How often?" answer, then one visit. A monthly price with
 * "How often?" left blank is still a weekly visit, not a one-off.
 */
export function bookingFrequency(
  accepted: string | null | undefined,
  preferred: string | null | undefined,
  billingBasis?: string | null | undefined,
  visitsPerWeek?: number | null,
): SeriesFrequency {
  // A monthly price is spread across weekly visits. A monthly or biweekly
  // schedule would drop visits while each one is still priced as a week’s share.
  if (billingBasis === 'monthly') return 'weekly';
  if (isSeriesFrequency(accepted)) return accepted;
  if (isSeriesFrequency(preferred)) return preferred;
  return 'one_time';
}

export interface QuoteBookingInput {
  frequency: SeriesFrequency;
  /** Visits a week a monthly price covers. Ignored unless the schedule is weekly. */
  visitsPerWeek: number | null;
  /**
   * A quote booked the old way, one weekday per schedule, still owes those
   * one-day schedules. More than 1 means this booking is a single weekday.
   */
  seriesClaimCeiling: number | null;
  startsOn: string;
}

/**
 * The stored recurrence for a quote that was just accepted.
 *
 * Weekly work that comes several times a week is one schedule with those
 * weekdays, not several schedules. A one-off is just the day they picked.
 */
export function storedBookingRule(input: QuoteBookingInput): { frequency: SeriesFrequency } & StoredRuleColumns {
  const startDow = dayOfWeekOf(input.startsOn);
  const several =
    input.frequency === 'weekly' &&
    (input.seriesClaimCeiling == null || input.seriesClaimCeiling <= 1) &&
    (input.visitsPerWeek ?? 1) > 1;
  const days = several ? weekdaysStartingOn(startDow, input.visitsPerWeek ?? 1) : null;
  const stored = normalizeStoredRule({
    frequency: input.frequency,
    dayOfWeek: input.frequency === 'one_time' || input.frequency === 'monthly' || days ? null : startDow,
    dayOfMonth: input.frequency === 'monthly' ? Number(input.startsOn.slice(8, 10)) : null,
    intervalWeeks: input.frequency === 'biweekly' ? 2 : input.frequency === 'weekly' ? 1 : null,
    daySlots: days ? days.map((dayOfWeek) => ({ dayOfWeek })) : null,
  });
  return { frequency: input.frequency, ...stored };
}

/** "9:00 AM" in English, "09:00" in Spanish. */
export function plainTime(hhmm: string, language: 'en' | 'es'): string {
  const match = TIME_PATTERN.exec(hhmm);
  if (!match) return hhmm;
  if (language === 'es') return hhmm;
  const hours = Number(match[1]);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour = hours % 12 || 12;
  return `${hour}:${match[2]} ${suffix}`;
}
